import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Check, X, Minus, Clock, Users, BarChart3, Play, Square, Mail, Edit3, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VotingPanel = ({ resolution, token, userRole, onVoteComplete, agStatus, hasConfirmedParticipation, participationMode, onResolutionUpdated }) => {
  const [voteStatus, setVoteStatus] = useState('pending');
  const [selectedVote, setSelectedVote] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const closeVoteRef = useRef(null);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitre, setEditedTitre] = useState(resolution.titre || '');
  const [editedDescription, setEditedDescription] = useState(resolution.description || '');
  const [editedMajorite, setEditedMajorite] = useState(resolution.type_majorite || 'Simple - Art. 24');
  const [saving, setSaving] = useState(false);

  // Determine if user can vote (timer active OR vote par correspondance mode)
  const canVote = (isTimerActive || (participationMode === 'correspondance' && voteStatus !== 'closed')) && userRole === 'coproprietaire';

  // Fetch current results
  const fetchResults = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/resolutions/${resolution.id}/results`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResults(response.data);
      
      if (resolution.vote_status === 'closed' || resolution.resultat !== 'En attente') {
        setVoteStatus('closed');
      }
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  }, [resolution.id, resolution.vote_status, resolution.resultat, token]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Close vote function
  const handleCloseVote = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API}/resolutions/${resolution.id}/close-vote`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVoteStatus('closed');
      setIsTimerActive(false);
      await fetchResults();
      if (onVoteComplete) {
        onVoteComplete(response.data);
      }
    } catch (error) {
      console.error('Error closing vote:', error);
    } finally {
      setLoading(false);
    }
  }, [resolution.id, token, fetchResults, onVoteComplete]);

  // Store ref to avoid stale closure
  useEffect(() => {
    closeVoteRef.current = handleCloseVote;
  }, [handleCloseVote]);

  // Timer countdown
  useEffect(() => {
    let interval;
    if (isTimerActive && timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => {
          if (prev <= 1) {
            setIsTimerActive(false);
            if (closeVoteRef.current) {
              closeVoteRef.current();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerActive, timer]);

  // Start vote
  const handleStartVote = async (duration = 60) => {
    try {
      setLoading(true);
      await axios.post(
        `${API}/resolutions/${resolution.id}/start-vote`,
        { duration },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVoteStatus('voting');
      setTimer(duration);
      setIsTimerActive(true);
    } catch (error) {
      console.error('Error starting vote:', error);
    } finally {
      setLoading(false);
    }
  };

  // Submit vote
  const handleVote = async (choice) => {
    try {
      setLoading(true);
      await axios.post(
        `${API}/resolutions/${resolution.id}/vote`,
        { choice, tantiemes: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedVote(choice);
      setVoteStatus('voted');
      await fetchResults();
    } catch (error) {
      console.error('Error voting:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMajorityDescription = (type) => {
    if (!type) return { label: 'Simple', desc: '', fullDesc: '', color: 'bg-gray-100 text-gray-700' };
    
    if (type.includes('Art. 24') || type.includes('Simple')) {
      return { 
        label: 'Art. 24', 
        desc: 'Majorité Simple', 
        fullDesc: 'Majorité des voix exprimées des coproprietaires présents, représentés ou ayant voté par correspondance',
        color: 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
      };
    }
    if (type.includes('Art. 25-1') || type.includes('Passerelle')) {
      return { 
        label: 'Art. 25-1', 
        desc: 'Passerelle', 
        fullDesc: 'Second vote sous Art. 24 après échec Art. 25 (si ≥1/3 des voix obtenues)',
        color: 'bg-amber-100 text-amber-700 border border-amber-200' 
      };
    }
    if (type.includes('Art. 25') || type.includes('Absolue')) {
      return { 
        label: 'Art. 25', 
        desc: 'Majorité Absolue', 
        fullDesc: 'Majorité des voix de TOUS les coproprietaires',
        color: 'bg-orange-100 text-orange-700 border border-orange-200' 
      };
    }
    if (type.includes('Art. 26') || type.includes('Double')) {
      return { 
        label: 'Art. 26', 
        desc: 'Double Majorité', 
        fullDesc: 'Majorité des membres du syndicat représentant au moins 2/3 des voix',
        color: 'bg-red-100 text-red-700 border border-red-200' 
      };
    }
    if (type.includes('Unanimité')) {
      return { 
        label: 'Unanimité', 
        desc: 'Vote unanime requis', 
        fullDesc: 'Décisions portant atteinte aux droits de propriété ou modifiant la consistance des lots',
        color: 'bg-purple-100 text-purple-700 border border-purple-200' 
      };
    }
    return { label: type, desc: '', fullDesc: '', color: 'bg-gray-100 text-gray-700' };
  };

  const majorityInfo = getMajorityDescription(resolution.type_majorite);
  
  // Save resolution edits
  const saveResolutionEdits = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/resolutions/${resolution.id}`,
        {
          titre: editedTitre,
          description: editedDescription,
          type_majorite: editedMajorite
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsEditing(false);
      if (onResolutionUpdated) {
        onResolutionUpdated({
          ...resolution,
          titre: editedTitre,
          description: editedDescription,
          type_majorite: editedMajorite
        });
      }
    } catch (error) {
      console.error('Error saving resolution:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditedTitre(resolution.titre || '');
    setEditedDescription(resolution.description || '');
    setEditedMajorite(resolution.type_majorite || 'Simple - Art. 24');
    setIsEditing(false);
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const calculatePercentages = () => {
    if (!results?.results) return { pour: 0, contre: 0, abstention: 0 };
    const total = (results.results.pour?.tantiemes || 0) + (results.results.contre?.tantiemes || 0) + (results.results.abstention?.tantiemes || 0);
    if (total === 0) return { pour: 0, contre: 0, abstention: 0 };
    return {
      pour: Math.round(((results.results.pour?.tantiemes || 0) / total) * 100),
      contre: Math.round(((results.results.contre?.tantiemes || 0) / total) * 100),
      abstention: Math.round(((results.results.abstention?.tantiemes || 0) / total) * 100)
    };
  };

  const percentages = calculatePercentages();

  // Majority options for select
  const majorityOptions = [
    'Simple - Art. 24',
    'Absolue - Art. 25',
    'Passerelle - Art. 25-1',
    'Double majorité - Art. 26',
    'Unanimité'
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        {isEditing ? (
          /* Edit mode */
          <div className="flex-1 space-y-3 mr-4">
            <input
              type="text"
              value={editedTitre}
              onChange={(e) => setEditedTitre(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-medium"
              placeholder="Titre de la résolution"
            />
            <textarea
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              rows={3}
              placeholder="Description de la résolution"
            />
            <select
              value={editedMajorite}
              onChange={(e) => setEditedMajorite(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              {majorityOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <div className="flex space-x-2">
              <button
                onClick={saveResolutionEdits}
                disabled={saving}
                className="flex items-center space-x-1 bg-[#00D3A8] hover:bg-[#00C7A1] text-white px-3 py-1.5 rounded-lg text-sm disabled:opacity-50"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <Save size={16} />
                )}
                <span>Enregistrer</span>
              </button>
              <button
                onClick={cancelEdit}
                className="flex items-center space-x-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-sm"
              >
                <X size={16} />
                <span>Annuler</span>
              </button>
            </div>
          </div>
        ) : (
          /* View mode */
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <span className="font-bold text-gray-800">Résolution {resolution.numero}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${majorityInfo.color}`} title={majorityInfo.fullDesc}>
                {majorityInfo.label}
              </span>
              {/* Edit button for gestionnaire during AG */}
              {userRole === 'gestionnaire' && (agStatus === 'En cours' || agStatus === 'Ouverte') && voteStatus !== 'closed' && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-gray-400 hover:text-[#00D3A8] transition-colors"
                  title="Modifier la résolution"
                >
                  <Edit3 size={16} />
                </button>
              )}
            </div>
            <h3 className="font-medium text-gray-700">{resolution.titre}</h3>
            {resolution.description && (
              <p className="text-sm text-gray-600 mt-1">{resolution.description}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">{majorityInfo.desc}</p>
            {majorityInfo.fullDesc && (
              <p className="text-xs text-gray-400 mt-0.5 italic">{majorityInfo.fullDesc}</p>
            )}
          </div>
        )}
        
        {!isEditing && (
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            voteStatus === 'closed' 
              ? resolution.resultat === 'Adoptée' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              : isTimerActive ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
          }`}>
            {voteStatus === 'closed' ? resolution.resultat : isTimerActive ? 'Vote en cours' : 'En attente'}
          </div>
        )}
      </div>

      {/* Timer */}
      {isTimerActive && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-yellow-700">
            <Clock size={20} />
            <span className="font-medium">Temps restant</span>
          </div>
          <div className="text-2xl font-bold text-yellow-700 font-mono">{formatTime(timer)}</div>
        </div>
      )}

      {/* Voting buttons */}
      {canVote && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 font-medium">Votre vote :</p>
            {participationMode === 'correspondance' && !isTimerActive && (
              <span className="flex items-center text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                <Mail size={12} className="mr-1" />
                Vote par correspondance
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => handleVote('Pour')}
              disabled={loading || selectedVote}
              className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center ${
                selectedVote === 'Pour' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-400'
              }`}
              data-testid="vote-pour-btn"
            >
              <Check size={24} className="text-green-500" />
              <span className="font-medium text-green-700">Pour</span>
            </button>
            <button
              onClick={() => handleVote('Contre')}
              disabled={loading || selectedVote}
              className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center ${
                selectedVote === 'Contre' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-red-400'
              }`}
              data-testid="vote-contre-btn"
            >
              <X size={24} className="text-red-500" />
              <span className="font-medium text-red-700">Contre</span>
            </button>
            <button
              onClick={() => handleVote('Abstention')}
              disabled={loading || selectedVote}
              className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center ${
                selectedVote === 'Abstention' ? 'border-gray-500 bg-gray-50' : 'border-gray-200 hover:border-gray-400'
              }`}
              data-testid="vote-abstention-btn"
            >
              <Minus size={24} className="text-gray-500" />
              <span className="font-medium text-gray-700">Abstention</span>
            </button>
          </div>
          {selectedVote && <div className="text-center text-sm text-green-600">✓ Vote enregistré</div>}
        </div>
      )}

      {/* Show message for non-voting users */}
      {userRole === 'coproprietaire' && !canVote && voteStatus !== 'closed' && !selectedVote && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center text-sm text-gray-600">
          {!hasConfirmedParticipation ? (
            <span>Confirmez votre participation pour pouvoir voter</span>
          ) : participationMode !== 'correspondance' && !isTimerActive ? (
            <span>Le vote sera ouvert par le gestionnaire pendant l'AG</span>
          ) : null}
        </div>
      )}

      {/* Results */}
      {results && (voteStatus === 'closed' || userRole === 'gestionnaire') && (
        <div className="space-y-3 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-2 text-gray-600">
              <BarChart3 size={16} />
              <span>Résultats</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <Users size={16} />
              <span>{results.total_voters || 0} votants</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-green-700 font-medium">Pour</span>
                <span className="text-gray-600">{results.results?.pour?.count || 0} ({results.results?.pour?.tantiemes || 0}t)</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-green-500" style={{ width: `${percentages.pour}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-red-700 font-medium">Contre</span>
                <span className="text-gray-600">{results.results?.contre?.count || 0} ({results.results?.contre?.tantiemes || 0}t)</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-red-500" style={{ width: `${percentages.contre}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-700 font-medium">Abstention</span>
                <span className="text-gray-600">{results.results?.abstention?.count || 0} ({results.results?.abstention?.tantiemes || 0}t)</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gray-400" style={{ width: `${percentages.abstention}%` }} />
              </div>
            </div>
          </div>
          
          {results.majority && (
            <div className={`p-3 rounded-lg ${results.majority.is_adopted ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <span className={results.majority.is_adopted ? 'text-green-700 text-sm' : 'text-red-700 text-sm'}>
                {results.majority.details}
              </span>
              {/* Afficher info passerelle Art. 25-1 si applicable */}
              {results.majority.passerelle_eligible && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm">
                  <strong>⚡ Passerelle Art. 25-1 disponible :</strong> {results.majority.passerelle_message}
                </div>
              )}
              {results.majority.passerelle_message && !results.majority.passerelle_eligible && !results.majority.is_adopted && results.majority.majority_type?.includes('Art. 25') && (
                <div className="mt-2 text-xs text-gray-500">
                  {results.majority.passerelle_message}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Gestionnaire controls */}
      {userRole === 'gestionnaire' && voteStatus !== 'closed' && (
        <div className="pt-3 border-t border-gray-200">
          {!isTimerActive ? (
            <div className="flex items-center space-x-3">
              <button onClick={() => handleStartVote(60)} disabled={loading}
                className="flex-1 bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-2 px-4 rounded-lg flex items-center justify-center space-x-2">
                <Play size={18} /><span>Démarrer (1 min)</span>
              </button>
              <button onClick={() => handleStartVote(120)} disabled={loading}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg">2 min</button>
              <button onClick={() => handleStartVote(180)} disabled={loading}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg">3 min</button>
            </div>
          ) : (
            <button onClick={handleCloseVote} disabled={loading}
              className="w-full bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg flex items-center justify-center space-x-2">
              <Square size={18} /><span>Clôturer maintenant</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default VotingPanel;
